import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Grid, Paper } from '@mui/material';
import { useTeamStats, useGameSession } from '../hooks/useGameSession';
import { TeamStats } from '../types';

const TeamColorMap: Record<string, string> = {
  red: '#FF6B6B',
  blue: '#4DABF7',
  green: '#51CF66',
  yellow: '#FFD43B',
  purple: '#9775FA',
  orange: '#FF922B'
};

interface GroupRankingProps {
  maxRankToShow?: number;
}

export const GroupRanking: React.FC<GroupRankingProps> = ({ maxRankToShow: propMaxRankToShow }) => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { stats } = useTeamStats(sessionId || '');
  const { session } = useGameSession(sessionId || '');
  const [displayStats, setDisplayStats] = useState<TeamStats[]>([]);
  const [animatedStats, setAnimatedStats] = useState<TeamStats[]>([]);

  // スムーズなカウントアップアニメーション
  useEffect(() => {
    if (stats.length === 0) return;

    // 初期値設定
    if (animatedStats.length === 0) {
      setAnimatedStats(stats.map(s => ({ ...s, scorePercentage: 0 })));
      setDisplayStats(stats);
    }

    // アニメーション
    const animationDuration = 1000; // 1秒
    const steps = 30;
    const stepDuration = animationDuration / steps;
    
    let currentStep = 0;
    const startStats = animatedStats.length > 0 ? [...animatedStats] : stats.map(s => ({ ...s, scorePercentage: 0 }));

    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      
      const newAnimatedStats = stats.map((targetStat, index) => {
        const startStat = startStats.find(s => s.teamColor === targetStat.teamColor) || { ...targetStat, scorePercentage: 0 };
        const animatedPercentage = startStat.scorePercentage + (targetStat.scorePercentage - startStat.scorePercentage) * progress;
        
        return {
          ...targetStat,
          scorePercentage: animatedPercentage
        };
      });
      
      setAnimatedStats(newAnimatedStats);
      setDisplayStats(newAnimatedStats);
      
      if (currentStep >= steps) {
        clearInterval(timer);
        setDisplayStats(stats);
        setAnimatedStats(stats);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [stats]);

  // 表示設定に応じてデータを準備
  const getRankingData = () => {
    // プロップスで渡されたmaxRankToShowを優先、次にsession設定
    const maxRankToShow = propMaxRankToShow || (session?.settings as any)?.maxRankToShow;
    const showTop3Only = session?.settings?.showTop3Only || false;
    const maxRank = maxRankToShow || (showTop3Only ? 3 : 6);
    
    console.log('GroupRanking - getRankingData:', {
      propMaxRankToShow,
      maxRankToShow,
      showTop3Only,
      maxRank,
      settingsFromSession: session?.settings
    });
    
    const result = [];
    
    for (let i = 0; i < maxRank; i++) {
      if (i < displayStats.length) {
        result.push({
          rank: i + 1,
          team: displayStats[i],
          percentage: displayStats[i].scorePercentage || 0
        });
      } else {
        result.push({
          rank: i + 1,
          team: null,
          percentage: 0
        });
      }
    }
    
    return result;
  };
  
  // 実際の表示データを取得（メモ化）
  const rankingData = React.useMemo(() => getRankingData(), [displayStats, session?.settings, propMaxRankToShow]);
  
  // 表示モードを取得
  const displayMode = React.useMemo(() => {
    const dataLength = rankingData.length;
    if (dataLength <= 3) return 'single-column';
    if (dataLength === 4) return 'grid-2x2';
    return 'two-columns';
  }, [rankingData.length]);

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        bgcolor: '#365964',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.2) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.2) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          pointerEvents: 'none'
        }
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          left: 40,
          color: 'white'
        }}
      >
        <Typography variant="h6">
          GroupRanking {new Date().toLocaleDateString('ja-JP')}
        </Typography>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          top: 20,
          right: 40,
          color: 'white'
        }}
      >
        <Typography variant="h6">
          NAZOTOKI KOSHOKAI
        </Typography>
      </Box>

      {/* メインコンテンツ */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 16
        }}
      >
        <Box sx={{ maxWidth: 1600, width: '100%' }}>
          {displayMode === 'single-column' ? (
            // 1-3位のみ表示モード
            <Grid container spacing={9}>
              {rankingData.map((data) => (
                <Grid item xs={12} key={data.rank}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      gap: 3
                    }}
                  >
                    <Box sx={{ 
                      minWidth: 320,
                      display: 'flex',
                      alignItems: 'baseline'
                    }}>
                      <Typography
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '8rem',
                          lineHeight: 1
                        }}
                      >
                        {data.rank}
                      </Typography>
                      <Typography
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '4.5rem',
                          lineHeight: 1,
                          ml: 0.5
                        }}
                      >
                        {data.rank === 1 ? 'st' : data.rank === 2 ? 'nd' : data.rank === 3 ? 'rd' : 'th'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'baseline' }}>
                      <Typography
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '8rem',
                          lineHeight: 1
                        }}
                      >
                        {Math.floor(data.percentage)}
                      </Typography>
                      <Typography
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '5rem',
                          lineHeight: 1
                        }}
                      >
                        .{Math.round((data.percentage % 1) * 100).toString().padStart(2, '0')}%
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : displayMode === 'grid-2x2' ? (
            // 1-4位表示モード（2x2グリッド）
            <Grid container spacing={6}>
              {/* 左列: 1,2位 */}
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {rankingData.slice(0, 2).map((data) => (
                    <Box
                      key={data.rank}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      gap: 3
                    }}
                  >
                    <Box sx={{ 
                      minWidth: 250,
                      display: 'flex',
                      alignItems: 'baseline'
                    }}>
                      <Typography
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '7rem',
                          lineHeight: 1
                        }}
                      >
                        {data.rank}
                      </Typography>
                      <Typography
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '4rem',
                          lineHeight: 1,
                          ml: 0.5
                        }}
                      >
                        {data.rank === 1 ? 'st' : data.rank === 2 ? 'nd' : data.rank === 3 ? 'rd' : 'th'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'baseline' }}>
                      <Typography
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '7rem',
                          lineHeight: 1
                        }}
                      >
                        {Math.floor(data.percentage)}
                      </Typography>
                      <Typography
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '4.5rem',
                          lineHeight: 1
                        }}
                      >
                        .{Math.round((data.percentage % 1) * 100).toString().padStart(2, '0')}%
                      </Typography>
                    </Box>
                  </Box>
                  ))}
                </Box>
              </Grid>
              
              {/* 右列: 3,4位 */}
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {rankingData.slice(2, 4).map((data) => (
                    <Box
                      key={data.rank}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: 3
                      }}
                    >
                      <Box sx={{ 
                        minWidth: 250,
                        display: 'flex',
                        alignItems: 'baseline'
                      }}>
                        <Typography
                          sx={{
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '7rem',
                            lineHeight: 1
                          }}
                        >
                          {data.rank}
                        </Typography>
                        <Typography
                          sx={{
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '4rem',
                            lineHeight: 1,
                            ml: 0.5
                          }}
                        >
                          {data.rank === 1 ? 'st' : data.rank === 2 ? 'nd' : data.rank === 3 ? 'rd' : 'th'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ flex: 1, display: 'flex', alignItems: 'baseline' }}>
                        <Typography
                          sx={{
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '7rem',
                            lineHeight: 1
                          }}
                        >
                          {Math.floor(data.percentage)}
                        </Typography>
                        <Typography
                          sx={{
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '4.5rem',
                            lineHeight: 1
                          }}
                        >
                          .{Math.round((data.percentage % 1) * 100).toString().padStart(2, '0')}%
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          ) : (
            // 1-6位表示モード（左右に分割）
            <Box sx={{ display: 'flex', gap: 8 }}>
              {/* 左側: 1-3位 */}
              <Box sx={{ flex: 1 }}>
                {rankingData.slice(0, 3).map((data) => (
                  <Box
                    key={data.rank}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      gap: 3,
                      mb: 6
                    }}
                  >
                    <Box sx={{ 
                      minWidth: 250,
                      display: 'flex',
                      alignItems: 'baseline'
                    }}>
                      <Typography
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '7rem',
                          lineHeight: 1
                        }}
                      >
                        {data.rank}
                      </Typography>
                      <Typography
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '4rem',
                          lineHeight: 1,
                          ml: 0.5
                        }}
                      >
                        {data.rank === 1 ? 'st' : data.rank === 2 ? 'nd' : data.rank === 3 ? 'rd' : 'th'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'baseline' }}>
                      <Typography
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '7rem',
                          lineHeight: 1
                        }}
                      >
                        {Math.floor(data.percentage)}
                      </Typography>
                      <Typography
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '4.5rem',
                          lineHeight: 1
                        }}
                      >
                        .{Math.round((data.percentage % 1) * 100).toString().padStart(2, '0')}%
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
              
              {/* 右側: 4-6位 */}
              <Box sx={{ flex: 1 }}>
                {rankingData.slice(3).map((data) => (
                  <Box
                    key={data.rank}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      gap: 3,
                      mb: 6
                    }}
                  >
                    <Box sx={{ 
                      minWidth: 200,
                      display: 'flex',
                      alignItems: 'baseline'
                    }}>
                      <Typography
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '6rem',
                          lineHeight: 1
                        }}
                      >
                        {data.rank}
                      </Typography>
                      <Typography
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '3.8rem',
                          lineHeight: 1,
                          ml: 0.5
                        }}
                      >
                        th
                      </Typography>
                    </Box>
                    
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'baseline' }}>
                      <Typography
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '6rem',
                          lineHeight: 1
                        }}
                      >
                        {Math.floor(data.percentage)}
                      </Typography>
                      <Typography
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '4rem',
                          lineHeight: 1
                        }}
                      >
                        .{Math.round((data.percentage % 1) * 100).toString().padStart(2, '0')}%
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* フッター */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 20,
          left: 40,
          color: 'white'
        }}
      >
        <Typography variant="body1">
          NAZOTOKI BATTLE ROYAL
        </Typography>
      </Box>

      {/* 装飾的な要素 */}
      <Box
        sx={{
          position: 'absolute',
          top: 60,
          left: 60,
          width: 40,
          height: 40,
          border: '2px solid white',
          borderBottom: 'none',
          borderRight: 'none'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 60,
          right: 60,
          width: 40,
          height: 40,
          border: '2px solid white',
          borderBottom: 'none',
          borderLeft: 'none'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 60,
          left: 60,
          width: 40,
          height: 40,
          border: '2px solid white',
          borderTop: 'none',
          borderRight: 'none'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 60,
          right: 60,
          width: 40,
          height: 40,
          border: '2px solid white',
          borderTop: 'none',
          borderLeft: 'none'
        }}
      />
    </Box>
  );
};